// Imports based on middleware
import { computed } from 'vue';
import { jwtDecode } from 'jwt-decode';
import Cookies from 'js-cookie';

// store imports
import { useAuthStore } from '@/stores/auth.js';

class Permissions {
    constructor() {
        this.roleHierarchy = {
            admin: 1,
            user: 2,
            guest: 3
        }
        this.cachedUserType = null;
    }

    #getTypeUser() {
        // Se abbiamo già il tipo utente in cache, lo restituiamo
        if (this.cachedUserType) {
            return this.cachedUserType;
        }

        try {
            // recupero il token jwt
            const jwtToken = Cookies.get('exampleJwt', { domain: import.meta.env.VITE_BASE_URL_COOKIES });

            // Se non c'è token, trattiamo l'utente come guest senza lanciare errori
            if (!jwtToken) {
                this.cachedUserType = 'guest';
                return this.cachedUserType;
            }

            const jwtData = jwtDecode(jwtToken);

            // Validazione del JWT e user_type
            if (!jwtData || !jwtData.user_type || !this.roleHierarchy[jwtData.user_type]) {
                this.cachedUserType = 'guest';
                return this.cachedUserType;
            }

            // Memorizziamo il tipo utente in cache
            this.cachedUserType = jwtData.user_type;
            return this.cachedUserType;

        } catch (error) {
            // In caso di errore, trattiamo l'utente come guest
            this.cachedUserType = 'guest';
            if (import.meta.env.VITE_DEVELOPMENT) {
                console.warn('Permessi: fallback a guest -', error.message);
            }
            return this.cachedUserType;
        }
    }

    #getRoleValue(role) {
        return typeof role === 'number' ? role : this.roleHierarchy[role] || this.roleHierarchy.guest;
    }

    #getOperatorValue(operator) {
        const operators = {
            gte: (a, b) => a >= b,
            eq: (a, b) => a === b,
            lte: (a, b) => a <= b
        };
        return operators[operator] || operators.gte;
    }

    hasPermission(requiredRole, operator = 'gte') {
        try {
            const userRole = this.#getTypeUser();
            const userRoleValue = this.#getRoleValue(userRole);
            const requiredRoleValue = this.#getRoleValue(requiredRole);
            const compare = this.#getOperatorValue(operator);

            return compare(userRoleValue, requiredRoleValue);
        } catch (error) {
            if (import.meta.env.VITE_DEVELOPMENT) {
                console.warn('Controllo permessi fallito, accesso negato -', error.message);
            }
            return false;
        }
    }

    handleRoute(to, from, next) {
        const isLoggedIn = computed(() => useAuthStore().getAuthStatus)?.value;

        // Se la rotta richiede autenticazione
        if (to.meta.requiresAuth) {
            if (!isLoggedIn) {
                return next({ name: 'login' });
            }

            // Controllo permessi solo se specificati
            if (to.meta.requiredRole && !this.hasPermission(to.meta.requiredRole)) {
                return next({ name: 'home' });
            }

            return next();
        }

        // Gestione rotte pubbliche
        if (isLoggedIn && ['login', 'recoverPassword'].includes(to.name)) {
            return next({ name: 'home' });
        }

        return next();
    }

    clearCache() {
        this.cachedUserType = null;
    }

    getRoleHierarchy() {
        return { ...this.roleHierarchy };
    }
}

const permissions = new Permissions();
export default permissions; 